import React, { useState } from "react";
import "./CheckBox.scss";
import ClassNames from "classnames";
import Highlighter from "react-highlight-words";
import { Tooltip, FieldViewBlock } from "../../components";
import { getDataAnalyticsProps } from "../../../utils";
import { FormFieldError, DragHandle } from "../../controls";

export interface WithTooltipProps {
  content?: string;
  classname?: string;
  delayShow?: number;
}

export interface ErrorProps {
  message: string;
  type: string;
}

export interface CheckBoxProps {
  checked?: boolean;
  wrapperClassName?: string;
  label?: string | React.ReactNode;
  error?: ErrorProps;
  className?: string;
  name?: string;
  onChange?: (value: any, isChecked: boolean) => void;
  value?: any;
  indeterminate?: boolean;
  disabled?: boolean;
  icon?: string;
  customPrefix?: React.ReactNode;
  customSuffix?: React.ReactNode;
  variant?: CheckBoxVariant;
  isLoading?: boolean;
  noMargin?: boolean;
  withHelp?: string;
  withTooltip?: WithTooltipProps | false;
  onBlur?: () => void;
  onFocus?: () => void;
  isLarge?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  withBg?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  alignLeft?: boolean;
  "data-testid"?: string;
  sortable?: boolean;
  searchValue?: string;
  viewMode?: boolean;
  description?: string;
}

export enum CheckBoxVariant {
  // eslint-disable-next-line no-unused-vars
  CHECKBOX = "checkbox",
  // eslint-disable-next-line no-unused-vars
  TOGGLE = "toggle",
  // eslint-disable-next-line no-unused-vars
  BUTTON = "button",
}

const CheckBox: React.FC<CheckBoxProps> = ({
  checked = false,
  wrapperClassName = "",
  label = "",
  error,
  className,
  name,
  onChange = () => {},
  value,
  indeterminate = false,
  disabled = false,
  icon,
  customPrefix,
  customSuffix,
  variant = CheckBoxVariant.CHECKBOX,
  isLoading = false,
  noMargin = false,
  withHelp = "",
  withTooltip = {},
  onBlur = () => {},
  onFocus = () => {},
  isLarge = false,
  inputRef,
  withBg = false,
  required = false,
  hideLabelHelper = true,
  alignLeft = false,
  "data-testid": dataTestId,
  sortable = false,
  searchValue,
  viewMode = false,
  description = "",
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);

  // Normalize the tooltip prop: callers may pass `false` (from a `cond && {...}`
  // expression). `false`/`undefined`/`{}` all collapse to no tooltip, matching
  // the prior runtime behavior where only a truthy `content` rendered a tip.
  const tooltipProps: WithTooltipProps = withTooltip || {};

  const classNameWrapper = ClassNames(
    "input-wrap",
    "checkbox-input-wrap",
    wrapperClassName && wrapperClassName,
    {
      "has-tip": !!tooltipProps?.content,
      "is-checked": checked,
      "is-unchecked": !checked && !indeterminate,
      "is-indeterminate": indeterminate,
      "has-label": label,
      "has-error": error,
      "is-sortable": sortable,
      "is-disabled": disabled,
      "has-icon": icon,
      "hide-label-helper": hideLabelHelper,
      "is-large": isLarge,
      "has-no-margin": noMargin,
      "toggle-style": variant === CheckBoxVariant.TOGGLE,
      "checkbox-style": variant === CheckBoxVariant.CHECKBOX,
      "button-style": variant === CheckBoxVariant.BUTTON,
      "has-background": withBg,
      "has-align-left": alignLeft,
      "is-loading": isLoading,
    },
    className
  );

  const inputClassNameWrapper = ClassNames("input-label", "checkbox-input", {
    "toggle-input": variant === CheckBoxVariant.TOGGLE,
    "button-input": variant === CheckBoxVariant.BUTTON,
    loading: variant === CheckBoxVariant.TOGGLE && isLoading,
  });

  const handleChange = (cbValue: boolean, isChecked: boolean) => {
    onChange?.(cbValue, isChecked);
  };

  const onFocusChange = () => {
    if (isFocused) {
      onBlur?.();
    } else {
      onFocus?.();
    }
    setIsFocused(!isFocused);
  };

  return !viewMode ? (
    <Tooltip
      type="info"
      className={tooltipProps?.classname}
      content={tooltipProps?.content}
      delayShow={tooltipProps?.delayShow}
    >
      <div
        data-testid={`checkbox-wrapper-${dataTestId}`}
        className={classNameWrapper}
      >
        {sortable && <DragHandle />}
        <input
          data-testid={`${dataTestId}-checkbox`}
          className={inputClassNameWrapper}
          name={name}
          type="checkbox"
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={(e) => handleChange(value, e.target.checked)}
          onBlur={onFocusChange}
          onFocus={onFocusChange}
          ref={inputRef}
          {...getDataAnalyticsProps(props)}
        />
        {variant === CheckBoxVariant.TOGGLE && (
          <div className="checkbox-toggle">
            {isLoading && <i className="seem-icon-spinner spinner" />}
          </div>
        )}
        {variant === CheckBoxVariant.BUTTON && (
          <div className="checkbox-button">
            <i
              className={`seem-icon-${icon || "x-small"}  checkbox-button-icon`}
            />
          </div>
        )}
        <Tooltip content={typeof label === "string" ? label : ""}>
          <label htmlFor={name} className="checkbox-label">
            {!!icon && <i className={`checkbox-icon seem-icon ${icon}`} />}
            {!!customPrefix && (
              <div className="checkbox-custom-prefix">{customPrefix}</div>
            )}
            <div className={`checkbox-label-data`}>
              {typeof label === "string" ? (
                <Highlighter
                  highlightClassName={
                    disabled
                      ? "highlight-text-wrap-disabled"
                      : "highlight-text-wrap"
                  }
                  searchWords={!!searchValue ? [searchValue] : []}
                  autoEscape={true}
                  textToHighlight={label}
                />
              ) : (
                label
              )}
            </div>
            {!!customSuffix && (
              <div className="checkbox-custom-suffix">{customSuffix}</div>
            )}
            {!required && !hideLabelHelper && (
              <span className={"label-helper"}>Optional</span>
            )}
          </label>
        </Tooltip>

        {withHelp && (
          <div className="input-help-text">
            <Tooltip type="info" content={withHelp}>
              <i className={`seem-icon seem-icon-help`} />
            </Tooltip>
          </div>
        )}
        {!!description && (
          <div className={`input-description`}>{description}</div>
        )}
        {error && (
          <FormFieldError errorMessage={error.message} errorType={error.type} />
        )}
      </div>
    </Tooltip>
  ) : (
    <FieldViewBlock
      label={typeof label === "string" ? label : ""}
      text={checked ? "Yes" : "No"}
      data-testid={`${dataTestId}-checkbox`}
    />
  );
};

export default CheckBox;
